import { useEffect, useRef, useState } from "react";
import { FaBold, FaItalic, FaLink, FaListOl, FaListUl, FaUnderline } from "react-icons/fa";
import { IoCloseCircle } from "react-icons/io5";
import { MdCode, MdFormatClear, MdImage, MdRedo, MdUndo } from "react-icons/md";
import Style from "./index.module.css";

const EmailModal = ({
  show,
  fornecedor,
  recipientOptions,
  multipleRecipients = false,
  emailData,
  setEmailData,
  onClose,
  onSend,
  isLoading,
}) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [activeFormats, setActiveFormats] = useState({});

  const destinatarios = Array.isArray(emailData.destinatario)
    ? emailData.destinatario
    : emailData.destinatario
      ? [emailData.destinatario]
      : [];

  const options = recipientOptions || [
    fornecedor?.endele && { value: fornecedor.endele, label: fornecedor.endele },
    fornecedor?.endalt && { value: fornecedor.endalt, label: fornecedor.endalt },
  ].filter(Boolean);

  const filteredOptions = options.filter((option) => {
    const term = recipientSearch.trim().toLowerCase();
    if (!term) return true;

    return [option.label, option.name, option.email, option.type, option.value]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(term));
  });

  const selectedOptions = options.filter((option) => destinatarios.includes(option.value));
  const visibleSelectedOptions = selectedOptions.slice(0, 6);
  const hiddenSelectedCount = Math.max(selectedOptions.length - visibleSelectedOptions.length, 0);
  const recipientInitial = (option) => (option.name || option.label || option.email || option.value || "?")
    .trim()
    .charAt(0)
    .toUpperCase();

  useEffect(() => {
    if (!show || sourceMode || !editorRef.current) return;
    if (editorRef.current.innerHTML !== emailData.conteudo) {
      editorRef.current.innerHTML = emailData.conteudo || "";
    }
  }, [emailData.conteudo, show, sourceMode]);

  useEffect(() => {
    if (!show) return;

    const updateActiveFormats = () => {
      if (sourceMode) {
        setActiveFormats({ html: true });
        return;
      }

      const selection = document.getSelection();
      const isInsideEditor = editorRef.current && selection?.rangeCount
        ? editorRef.current.contains(selection.anchorNode)
        : false;

      if (!isInsideEditor) {
        setActiveFormats({});
        return;
      }

      const anchorElement = selection.anchorNode?.nodeType === Node.ELEMENT_NODE
        ? selection.anchorNode
        : selection.anchorNode?.parentElement;

      setActiveFormats({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
        unorderedList: document.queryCommandState("insertUnorderedList"),
        orderedList: document.queryCommandState("insertOrderedList"),
        link: !!anchorElement?.closest?.("a"),
      });
    };

    document.addEventListener("selectionchange", updateActiveFormats);
    return () => document.removeEventListener("selectionchange", updateActiveFormats);
  }, [show, sourceMode]);

  if (!show) return null;

  const updateConteudo = (conteudo) => {
    setEmailData((prev) => ({ ...prev, conteudo }));
  };

  const syncEditor = () => {
    updateConteudo(editorRef.current?.innerHTML || "");
  };

  const runCommand = (command, value = null) => {
    if (sourceMode) return;
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncEditor();
    setActiveFormats((prev) => ({ ...prev, [command]: document.queryCommandState(command) }));
  };

  const handleCreateLink = () => {
    const url = window.prompt("Informe a URL do link:");
    if (url) runCommand("createLink", url);
  };

  const handleImageFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => runCommand("insertImage", reader.result);
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const toggleRecipient = (value) => {
    setEmailData((prev) => {
      const selected = Array.isArray(prev.destinatario)
        ? prev.destinatario
        : prev.destinatario
          ? [prev.destinatario]
          : [];

      return {
        ...prev,
        destinatario: selected.includes(value)
          ? selected.filter((item) => item !== value)
          : [...selected, value],
      };
    });
  };

  const toggleSourceMode = () => {
    if (sourceMode && editorRef.current) {
      editorRef.current.innerHTML = emailData.conteudo || "";
    } else {
      syncEditor();
    }

    setSourceMode((prev) => !prev);
  };

  const selectAllRecipients = () => {
    setEmailData((prev) => ({
      ...prev,
      destinatario: options.map((option) => option.value),
    }));
  };

  const clearRecipients = () => {
    setEmailData((prev) => ({
      ...prev,
      destinatario: [],
    }));
  };

  const refreshFormatState = () => setActiveFormats({
    bold: document.queryCommandState("bold"),
    italic: document.queryCommandState("italic"),
    underline: document.queryCommandState("underline"),
    unorderedList: document.queryCommandState("insertUnorderedList"),
    orderedList: document.queryCommandState("insertOrderedList"),
  });

  const toolbarButtonClass = (isActive) => isActive ? Style.activeToolbarButton : "";

  return (
    <div className={Style.modalOverlay}>
      <div className={`${Style.modalContent} ${multipleRecipients ? Style.emailModalLarge : ""}`}>
        <div className={Style.modalHeader}>
          <h3>Enviar Email</h3>
          <button className={Style.modalCloseButton} onClick={onClose}>
            <IoCloseCircle />
          </button>
        </div>

        <div className={Style.modalBody}>
          <div className={Style.formGroup}>
            <label htmlFor="destinatario">Destinatário:</label>
            {multipleRecipients ? (
              <div className={Style.recipientPicker}>
                <div className={Style.recipientPickerHeader}>
                  <div className={Style.recipientSummary}>
                    <span className={Style.recipientSummaryNumber}>{destinatarios.length}</span>
                    <div>
                      <strong>Destinatários selecionados</strong>
                      <small>{options.length} email{options.length !== 1 ? "s" : ""} disponível{options.length !== 1 ? "s" : ""}</small>
                    </div>
                  </div>
                  <div className={Style.recipientActions}>
                    <button type="button" onClick={selectAllRecipients}>Todos</button>
                    <button type="button" onClick={clearRecipients}>Limpar</button>
                  </div>
                </div>

                {selectedOptions.length > 0 && (
                  <div className={Style.selectedRecipientsPanel}>
                    <span className={Style.selectedRecipientsLabel}>Para</span>
                    <div className={Style.selectedRecipientChips}>
                      {visibleSelectedOptions.map((option) => (
                        <button
                          type="button"
                          key={option.value}
                          className={Style.selectedRecipientChip}
                          onClick={() => toggleRecipient(option.value)}
                          title="Remover destinatario"
                        >
                          <span>{option.name || option.email || option.value}</span>
                          <strong>x</strong>
                        </button>
                      ))}
                      {hiddenSelectedCount > 0 && (
                        <span className={Style.moreRecipients}>+{hiddenSelectedCount}</span>
                      )}
                    </div>
                  </div>
                )}

                <div className={Style.recipientSearchRow}>
                  <input
                    className={Style.recipientSearch}
                    type="text"
                    placeholder="Buscar fornecedor ou email..."
                    value={recipientSearch}
                    onChange={(event) => setRecipientSearch(event.target.value)}
                  />
                  <span>{filteredOptions.length} resultado{filteredOptions.length !== 1 ? "s" : ""}</span>
                </div>

                <div className={Style.recipientList}>
                  {filteredOptions.map((option) => (
                    <button
                      type="button"
                      key={option.value}
                      className={`${Style.recipientCard} ${destinatarios.includes(option.value) ? Style.selectedRecipient : ""}`}
                      onClick={() => toggleRecipient(option.value)}
                    >
                      <span className={Style.recipientAvatar}>{recipientInitial(option)}</span>
                      <span className={Style.recipientText}>
                        <strong>{option.name || option.label}</strong>
                        <small>{option.email || option.value}</small>
                      </span>
                      {option.type && <span className={Style.recipientType}>{option.type}</span>}
                      <span className={Style.recipientCheck} />
                    </button>
                  ))}
                  {filteredOptions.length === 0 && (
                    <div className={Style.emptyRecipients}>Nenhum destinatário encontrado.</div>
                  )}
                </div>
              </div>
            ) : (
              <select
                id="destinatario"
                className={Style.formInput}
                value={emailData.destinatario}
                onChange={(e) =>
                  setEmailData((prev) => ({
                    ...prev,
                    destinatario: e.target.value,
                  }))
                }
              >
                <option value="" disabled>Selecione um email</option>
                {options.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            )}
          </div>

          <div className={Style.formGroup}>
            <label htmlFor="assunto">Assunto:</label>
            <input
              id="assunto"
              type="text"
              className={Style.formInput}
              placeholder="Digite o assunto do email..."
              value={emailData.assunto}
              onChange={(e) =>
                setEmailData((prev) => ({
                  ...prev,
                  assunto: e.target.value,
                }))
              }
            />
          </div>

          <div className={Style.formGroup}>
            <label htmlFor="conteudo">Conteúdo:</label>
            <div className={Style.editorToolbar}>
              <button type="button" title="Desfazer" onClick={() => runCommand("undo")}><MdUndo /></button>
              <button type="button" title="Refazer" onClick={() => runCommand("redo")}><MdRedo /></button>
              <button type="button" title="Negrito" className={toolbarButtonClass(activeFormats.bold)} onClick={() => runCommand("bold")}><FaBold /></button>
              <button type="button" title="Itálico" className={toolbarButtonClass(activeFormats.italic)} onClick={() => runCommand("italic")}><FaItalic /></button>
              <button type="button" title="Sublinhado" className={toolbarButtonClass(activeFormats.underline)} onClick={() => runCommand("underline")}><FaUnderline /></button>
              <button type="button" title="Lista" className={toolbarButtonClass(activeFormats.unorderedList)} onClick={() => runCommand("insertUnorderedList")}><FaListUl /></button>
              <button type="button" title="Lista numerada" className={toolbarButtonClass(activeFormats.orderedList)} onClick={() => runCommand("insertOrderedList")}><FaListOl /></button>
              <button type="button" title="Link" className={toolbarButtonClass(activeFormats.link)} onClick={handleCreateLink}><FaLink /></button>
              <button type="button" title="Imagem" onClick={() => fileInputRef.current?.click()}><MdImage /></button>
              <button type="button" title="Limpar formatação" onClick={() => runCommand("removeFormat")}><MdFormatClear /></button>
              <button
                type="button"
                title="Editar HTML"
                className={toolbarButtonClass(sourceMode)}
                onClick={toggleSourceMode}
              >
                <MdCode />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className={Style.hiddenInput}
                onChange={handleImageFile}
              />
            </div>
            {sourceMode ? (
              <textarea
                id="conteudo"
                className={Style.formTextarea}
                placeholder="Digite ou cole o HTML do email"
                rows={8}
                value={emailData.conteudo}
                onChange={(e) => updateConteudo(e.target.value)}
              />
            ) : (
              <div
                id="conteudo"
                ref={editorRef}
                className={Style.richEditor}
                contentEditable
                role="textbox"
                aria-multiline="true"
                data-placeholder="Digite o conteúdo do email..."
                onInput={syncEditor}
                onBlur={syncEditor}
                onKeyUp={refreshFormatState}
                onMouseUp={refreshFormatState}
              />
            )}
          </div>
        </div>

        <div className={Style.modalFooter}>
          <button
            className={Style.cancelButton}
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            className={Style.confirmButton}
            onClick={onSend}
            disabled={isLoading || destinatarios.length === 0}
          >
            {isLoading ? "Enviando..." : "Enviar Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export { EmailModal };
